import { api } from './client'

export interface AuthUser {
  id: number
  email: string
  role: string
  roles?: { code: string; name: string }[]
  /** Códigos de roles como lista de strings (ex.: ['devops', 'manager']).
   *  Use para verificações diretas: `user.role_codes?.includes('devops')`.
   *  SP3 Task 6: gate de UI de ação (ActionProposalCard). */
  role_codes?: string[]
  full_name?: string
  must_change_password?: boolean
  tenants: { id: number; name: string; environment: string; ou_id: string }[]
  global_permissions: string[]
  tenant_permissions: Record<string, string[]>
  // ── Insumos do gate de UI (TASK-9328), servidos pelo /auth/me ─────────────
  /** Superusuário: flag `is_admin` OU função `admin`. A UI olhava só a flag, e
   *  `has_permission` honra as duas. */
  is_superuser?: boolean
  /** `plataforma:global_view`: LEITURA em qualquer tenant. Mutação não. */
  global_read_all?: boolean
  /** Ações que a base concede mas o cap do tenant remove. tenant_id → códigos. */
  capped_tenants?: Record<string, string[]>
  /** `true` quando o usuário NÃO tem template aplicado: aí a concessão global
   *  (`user_tenant_permissions` com `tenant_id IS NULL`) vale para qualquer
   *  tenant, como em `has_permission`. Com template, a decisão sai do template e
   *  não há fallback global. */
  global_applies_to_tenants?: boolean
  // Perfil R3 — campos pessoais editaveis (todos opcionais; null se nunca preenchido)
  phone?: string | null
  address?: string | null
  employee_number?: string | null
  organization_id?: number | null
  job_title?: string | null
  /** IANA timezone (ex: 'America/Manaus'). null = usar tenant default → SERVER_DEFAULT. */
  timezone?: string | null
  avatar_url?: string | null    // presigned GET TTL 24h ou null se sem avatar
  avatar_supported?: boolean    // false → backend sem bucket (UI esconde botoes)
  // MR-D: TOTP/MFA + wizard
  has_totp?: boolean
  missing_required_fields?: string[]
  // Lote 7 ZTNA Wizard: feature flags entregues pelo backend
  feature_flags?: {
    ztna_wizard_enabled?: boolean
    rbac_template_caps_enabled?: boolean
    ztna_session_heartbeat_enabled?: boolean
    inactivity_logout_enabled?: boolean
    [key: string]: boolean | undefined
  }
  // ZTNA SSO alignment: URL base do broker p/ o heartbeat de sessão.
  ztna_broker_url?: string
  // RBAC v4 Fase 1 (TASK-2548): acesso efetivo via template + UTP — tela /perfil.
  // Opcional pra backward-compat com cache hidratado pre-deploy (campos undefined
  // ate refetch automatico).
  applied_template_id?: number | null
  applied_template_name?: string | null
  applied_template_perm_count?: number
  effective_perm_count?: number
  is_admin?: boolean
  last_seen_changelog_version?: string | null
}

export const authApi = {
  me: async () => {
    const res = await api.get<AuthUser>('/auth/me')
    return res.data
  },
  logout: async () => {
    await api.post('/auth/logout')
  },
  changePassword: async (currentPassword: string, newPassword: string) => {
    const res = await api.post('/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
    return res.data
  },
  forgotPassword: async (email: string) => {
    const res = await api.post('/v1/auth/forgot-password', { email })
    return res.data
  },
  validateResetToken: async (token: string) => {
    const res = await api.get('/v1/auth/reset-password/validate', { params: { token } })
    return res.data
  },
  resetPassword: async (token: string, newPassword: string) => {
    const res = await api.post('/v1/auth/reset-password', {
      token,
      new_password: newPassword,
    })
    return res.data
  },
  reportPlatformError: async (params: {
    token: string; errorCode?: string; pageUrl?: string; userAgent?: string
  }) => {
    const res = await api.post('/v1/auth/report-platform-error', {
      token: params.token,
      error_code: params.errorCode ?? null,
      page_url: params.pageUrl ?? null,
      user_agent: params.userAgent ?? null,
    })
    return res.data as {
      status: 'created' | 'exists' | 'refused'
      protocol_number?: string
      reason?: string
    }
  },
  forgotTotp: async (email: string) => {
    const res = await api.post<{
      masked_phone: string
      next_step: 'enter_code' | 'check_whatsapp' | 'contact_admin' | 'check_email' | 'send_failed'
    }>('/auth/forgot-totp', { email })
    return res.data
  },
  verifyForgotTotp: async (email: string, code: string) => {
    const res = await api.post<{ ok: boolean }>('/auth/forgot-totp/verify', { email, code })
    return res.data
  },
  validateSetupPhoneToken: async (token: string) => {
    const res = await api.get<{ email: string }>(
      `/auth/setup-phone/validate?token=${encodeURIComponent(token)}`,
    )
    return res.data
  },
  setupPhone: async (token: string, phone: string, password: string) => {
    const res = await api.post<{ ok: boolean }>(
      '/auth/setup-phone', { token, phone, password },
    )
    return res.data
  },
  markChangelogSeen: (version: string, medida?: { duracao_ms: number; itens: number }) =>
    api.post('/v1/me/changelog-seen', { version, ...medida }).then((r) => r.data),
}
